import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { setCrossDomainCookie, clearAllCookies, getCookie } from "../../../utils/cookie-utils";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import { isOlivsApiSuccess } from "../../../utils/api";
import {
  authxAppsBase,
  authxSessionHeaders,
  buildAuthxSessionGetUrl,
  getPendingVerificationPath,
  mergeAuthxVerifyMfaSession,
  isVerifyMfaResponseSuccess,
  persistAuthxSessionCookies,
  clearPendingVerificationState,
  shouldActivateCreatedUser,
  activateCreatedUser,
} from "../../../utils/authx-login-helpers";
import {
  getAuthxAppsErrorMessage,
  isAuthxAppsOperationSuccess,
  parseAuthxAppsResponseBody,
  isAuthxMfaNotEnabledBody,
  isAuthxSessionExpiredBody,
} from "../../../utils/authx-apps-response";
import envConfig from "../../../config";
import { enqueueSnackbar } from "notistack";
import { formatPhoneDisplay } from "../../../utils/formatPhone";
import ThemeToggle from "../../ThemeToggle/ThemeToggle";

const VerifyPhone = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [mobilePhoneNumber, setMobilePhoneNumber] = useState("");
  const [phoneNumberSaved, setPhoneNumberSaved] = useState("");
  const [code, setCode] = useState();
  const [loginSecuredHash, setLoginSecuredHash] = useState();
  const [accessKey, setAccessKey] = useState("");
  const [refreshKey, setRefreshKey] = useState("");

  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [phoneChecked, setPhoneChecked] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingPhoneLoading, setIsUpdatingPhoneLoading] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [isInitialSmsLoading, setIsInitialSmsLoading] = useState(false);

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (isUpdatingPhoneLoading) return;

    if (!mobilePhoneNumber || mobilePhoneNumber?.length < 5) {
      enqueueSnackbar("Invalid phone number.", { variant: "error" });
      return;
    }
    if (!mobilePhoneNumber?.startsWith("+61")) {
      enqueueSnackbar("Mobile phone number needs to start with +61", {
        variant: "error",
      });
      return;
    }

    setIsUpdatingPhoneLoading(true);

    try {
      const response = await fetch(
        `https://${envConfig.apiDev1}/api/v3/en-au/users/change-phone-number?BaseHostURL=${envConfig.loginUrl}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "olivs-api-real-ip": "127.0.0.2",
            "olivs-api-computer-name": "EC2AMAZ-QF1CQAD",
            "api-key": "OlivsAuth",
          },
          body: JSON.stringify({
            LoginSecuredHash: loginSecuredHash,
            newMobilePhoneNumber: mobilePhoneNumber,
          }),
        }
      );
      const data = await response.json();

      if (data.SuccessYN === "Y") {
        setPhoneNumberSaved(mobilePhoneNumber);
        setUpdatingPhone(false);
        enqueueSnackbar("Your phone number has been updated", {
          variant: "success",
        });
        setMobilePhoneNumber("");
      }
    } catch (error) {
      console.error("Error during phone update:", error);
    } finally {
      setIsUpdatingPhoneLoading(false);
    }
  };

  useEffect(() => {
    if (phoneChecked) return;

    const { accessKey: ak, refreshKey: rk, loginSecuredHash: hash, phone } =
      location.state ?? {};

    if (ak && rk && hash) {
      if (phone) {
        setPhoneNumberSaved(phone);
      }
      setLoginSecuredHash(hash);
      setAccessKey(ak);
      setRefreshKey(rk);
      setPhoneChecked(true);
      initialSendSms();
      return;
    }

    const cookieLoginSecured = getCookie("loginSecuredHash");
    const cookieAccessKey = getCookie("accessKey");
    const cookieRefreshKey = getCookie("refreshKey");

    if (
      cookieLoginSecured &&
      cookieAccessKey &&
      cookieRefreshKey &&
      getPendingVerificationPath() === "/verify-phone"
    ) {
      setLoginSecuredHash(cookieLoginSecured);
      setAccessKey(cookieAccessKey);
      setRefreshKey(cookieRefreshKey);
      setPhoneChecked(true);
      initialSendSms();
      return;
    }

    enqueueSnackbar("Session expired, please log in again.", {
      variant: "error",
    });
    navigate("/");
  }, [location.state, phoneChecked, navigate]);

  const verifyAll = async () => {
    const ak = accessKey || location.state?.accessKey;
    const rk = refreshKey || location.state?.refreshKey;
    const hash = loginSecuredHash || location.state?.loginSecuredHash;

    if (!hash || !ak || !rk) {
      enqueueSnackbar("Session expired, please log in again.", { variant: "error" });
      navigate("/");
      return false;
    }

    const activation = await activateCreatedUser(hash);

    if (activation.ok) {
      setCrossDomainCookie("accessKey", ak);
      setCrossDomainCookie("refreshKey", rk);
      setCrossDomainCookie("loginSecuredHash", hash);
      setCrossDomainCookie("phoneVerified", "Y");
      clearPendingVerificationState();
      navigate("/mfa-setup", {
        state: { accessKey: ak, refreshKey: rk },
      });
      return true;
    }

    enqueueSnackbar(
      activation.errorMessage || "Account activation failed. Please try again.",
      { variant: "error" },
    );
    return false;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!code || code?.length < 5) {
      enqueueSnackbar("Invalid verification code", { variant: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      const ak = accessKey || location.state?.accessKey;
      const rk = refreshKey || location.state?.refreshKey;
      const verificationUrl = `${authxAppsBase()}/verify-mfa`;

      const headers = authxSessionHeaders(ak, rk, "127.0.0.2");

      const verificationData = JSON.stringify({
        accessKey: ak,
        mfaCode: String(code),
        rememberMe: "N",
      });

      const verificationResponse = await fetch(verificationUrl, {
        method: "POST",
        headers: headers,
        body: verificationData,
      });

      const verificationBody = await parseAuthxAppsResponseBody(
        verificationResponse,
      );

      if (isAuthxMfaNotEnabledBody(verificationBody)) {
        setCrossDomainCookie("accessKey", ak);
        setCrossDomainCookie("refreshKey", rk);
        setCrossDomainCookie("loginSecuredHash", loginSecuredHash);
        setCrossDomainCookie("phoneVerified", "Y");
        clearPendingVerificationState();
        navigate("/mfa-setup", {
          state: { accessKey: ak, refreshKey: rk },
        });
        return;
      }

      if (isAuthxSessionExpiredBody(verificationBody)) {
        clearAllCookies();
        navigate("/");
        return;
      }

      if (typeof verificationBody === "string") {
        setCode("");
        return;
      }

      const verificationJson =
        typeof verificationBody === "object" && verificationBody
          ? verificationBody
          : {};
      const merged = mergeAuthxVerifyMfaSession(
        { accessKey: ak, refreshKey: rk },
        verificationJson,
      );
      const verifyOk = isVerifyMfaResponseSuccess(
        verificationResponse.ok,
        verificationJson,
        merged,
      );

      if (verifyOk) {
        persistAuthxSessionCookies(
          {
            ...verificationJson,
            accessKey: merged.accessKey,
            refreshKey: merged.refreshKey,
          },
          undefined,
        );

        if (shouldActivateCreatedUser(merged)) {
          const activated = await verifyAll();
          if (activated) {
            enqueueSnackbar(
              "Your phone number has been verified. Redirecting…",
              { variant: "success" },
            );
          }
          return;
        }

        const approveUrl = `https://${envConfig.apiDev1}/api/v3/en-au/users/approve-phone-number?BaseHostURL=${envConfig.loginUrl}`;

        const approveHeaders = {
          Accept: "application/json",
          "Content-Type": "application/json",
          "olivs-api-real-ip": "127.0.0.2",
          "olivs-api-computer-name": "EC2AMAZ-QF1CQAD",
          "api-key": "OlivsAuth",
        };

        const approveResponse = await fetch(approveUrl, {
          method: "POST",
          headers: approveHeaders,
          body: JSON.stringify({
            LoginSecuredHash: loginSecuredHash,
          }),
        });

        const approveJson = await approveResponse.json().catch(() => ({}));
        if (isOlivsApiSuccess(approveJson, approveResponse.ok)) {
          const activated = await verifyAll();
          if (activated) {
            enqueueSnackbar(
              "Your phone number has been verified. Redirecting…",
              { variant: "success" },
            );
          }
        }
      } else {
        setCode("");
      }
    } catch (error) {
      console.error("Error during verification:", error);
      setCode("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendCode = async () => {
    if (isResendingCode) return;

    setIsResendingCode(true);

    try {
      const ak = accessKey || location.state?.accessKey;
      const rk = refreshKey || location.state?.refreshKey;
      const smsUrl = buildAuthxSessionGetUrl("send-2fa-code-as-sms", ak, rk);

      const smsResponse = await fetch(smsUrl, {
        method: "GET",
        headers: authxSessionHeaders(ak, rk, "127.0.0.2"),
      });

      const smsJson = await smsResponse.json().catch(() => ({}));
      if (isAuthxAppsOperationSuccess(smsResponse.ok, smsJson)) {
        enqueueSnackbar("SMS has been sent.", { variant: "success" });
      } else {
        enqueueSnackbar(
          getAuthxAppsErrorMessage(
            smsJson,
            "Failed to send SMS code. Please try again.",
          ),
          { variant: "error" },
        );
      }
    } catch (error) {
      console.error("Error during SMS resend:", error);
    } finally {
      setIsResendingCode(false);
    }
  };

  const initialSendSms = async () => {
    setIsInitialSmsLoading(true);

    try {
      const ak = location.state?.accessKey;
      const rk = location.state?.refreshKey;
      const smsUrl = buildAuthxSessionGetUrl("send-2fa-code-as-sms", ak, rk);

      const headers = authxSessionHeaders(ak, rk, "127.0.0.2");

      const smsResponse = await fetch(smsUrl, {
        method: "GET",
        headers: headers,
      });

      const smsJson = await smsResponse.json().catch(() => ({}));
      if (!isAuthxAppsOperationSuccess(smsResponse.ok, smsJson)) {
        enqueueSnackbar(
          getAuthxAppsErrorMessage(
            smsJson,
            "Failed to send SMS code. Please try again.",
          ),
          { variant: "error" },
        );
      }
    } catch (error) {
      console.error("Error during initial SMS send:", error);
    } finally {
      setIsInitialSmsLoading(false);
    }
  };

  return (
    <Box className="glass-card" sx={{ p: { xs: 3.5, md: 4 }, width: "100%", position: "relative" }}>
      {isInitialSmsLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0, 0, 0, 0.4)",
            borderRadius: "24px",
            zIndex: 10,
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}

      <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
        Verify your phone
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Your e-mail address is verified!
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Now, let&apos;s verify your phone number: we&apos;ve sent a text message (SMS) with a verification code
        {phoneNumberSaved
          ? ` to your phone (${formatPhoneDisplay(phoneNumberSaved, false)})`
          : " to the mobile number you provided during registration"}
        . Please enter it below.
      </Typography>

      {!updatingPhone ? (
        <Box sx={{ mb: 2 }}>
          <Link
            component="button"
            type="button"
            className="link"
            onClick={() => setUpdatingPhone(true)}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 600,
              textDecoration: "none",
              background: "none",
              border: "none",
              p: 0,
            }}
            data-testid="verify-phone--Link-0"
          >
            Edit Phone Number
          </Link>
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            name="mobilePhoneNumber"
            value={mobilePhoneNumber}
            onChange={(e) => setMobilePhoneNumber(e.target.value)}
            label="Mobile Phone Number"
            required
            disabled={isUpdatingPhoneLoading}
            sx={{ flex: 1, minWidth: 180 }}
            data-testid="verify-phone--TextField-0"
          />
          <Button
            variant="outlined"
            onClick={handleUpdate}
            disabled={isUpdatingPhoneLoading}
            data-testid="verify-phone--Button-0"
          >
            {isUpdatingPhoneLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Update"
            )}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setUpdatingPhone(false)}
            disabled={isUpdatingPhoneLoading}
            data-testid="verify-phone--Button-1"
          >
            Cancel
          </Button>
        </Box>
      )}

      <TextField
        label="Verification Code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
        disabled={isSubmitting}
        fullWidth
        sx={{ mb: 3 }}
        inputProps={{
          style: {
            textAlign: "center",
            fontSize: "16px",
            letterSpacing: "5px",
          },
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isSubmitting) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        autoFocus
        data-testid="verify-phone--TextField-1"
      />

      <Button
        variant="outlined"
        className="btn-glass"
        onClick={handleSubmit}
        fullWidth
        disabled={isSubmitting}
        sx={{ mb: 3 }}
        data-testid="verify-phone--Button-2"
      >
        {isSubmitting ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Processing...
          </Box>
        ) : (
          "Sign Up"
        )}
      </Button>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          SMS didn&apos;t arrive?
        </Typography>
        <Link
          component="button"
          type="button"
          onClick={resendCode}
          className="link"
          disabled={isResendingCode}
          sx={{
            color: isResendingCode ? "text.disabled" : "primary.main",
            cursor: isResendingCode ? "default" : "pointer",
            fontSize: "14px",
            fontWeight: 600,
            textDecoration: "none",
            background: "none",
            border: "none",
            p: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
          }}
          data-testid="verify-phone--Link-1"
        >
          {isResendingCode ? (
            <>
              <CircularProgress size={16} />
              Sending...
            </>
          ) : (
            "Resend the SMS"
          )}
        </Link>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ThemeToggle title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"} />
      </Box>
    </Box>
  );
};

export default VerifyPhone;
